import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core";
import { I18n } from "@aws-amplify/core";
import {
  get,
  map,
  sortBy,
  forEach,
  concat,
  filter,
  uniq,
  includes,
} from "lodash";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import MenuItem from "@material-ui/core/MenuItem";

import MultiLevelSelectItem from "./MultiLevelSelectItem";
import colors from "../../assets/style/colors";

const styles = {
  menuItemTop: {
    maxHeight: "40px",
    cursor: "initial",
    paddingLeft: "12px",
    borderBottom: `1px solid ${colors.veryLighPink}`,
    "&:hover": {
      backgroundColor: colors.white,
    },
  },
  plusSymbol: {
    cursor: "pointer",
    color: colors.primary,
    fontSize: "1.6em",
  },
  textField: {
    margin: 0,
    backgroundColor: "white",
  },
  selectMenu: {
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  menuList: {
    padding: "8px 0px",
  },
  input: {
    height: "36px",
  },
  selectInput: {
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    fontSize: "0.875em",
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
        borderWidth: "1px",
      },
    },
  },
};

const MultiLevelSelect = (props) => {
  const {
    classes,
    name,
    values = [],
    onChange,
    dataAutomation,
    disabled,
    noSort,
    options,
  } = props;

  const [open, setOpen] = React.useState(false);

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    if (props.onClose) {
      props.onClose();
    }
    setOpen(false);
  };

  const isChecked = (item) => {
    if (get(item, ["options"])) {
      let checked = true;
      forEach(get(item, ["options"]), (opt) => {
        checked = checked && isChecked(opt);
      });
      return checked;
    } else {
      if (includes(values, item.id)) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isIndeterminate = (item) => {
    if (get(item, ["options"])) {
      let any = false;
      forEach(get(item, ["options"]), (opt) => {
        any = any || isIndeterminate(opt);
      });
      return any;
    } else {
      if (includes(values, item.id)) {
        return true;
      } else {
        return false;
      }
    }
  };

  let checked = isChecked({ options });
  let indeterminate = isIndeterminate({ options }) && !checked;

  const handleChange = (item) => {
    let newValues;
    if (get(item, ["options"])) {
      if (checked) {
        newValues = unselectItem(item, values);
      } else {
        newValues = selectItem(item, values);
      }
    }
    onChange(uniq(newValues));
  };

  const selectItem = (item, values) => {
    if (get(item, ["options"])) {
      let newValues = [...values];
      forEach(get(item, ["options"]), (opt) => {
        newValues = concat(newValues, selectItem(opt, values));
      });
      return newValues;
    } else {
      return [item.id];
    }
  };

  const unselectItem = (item, values) => {
    if (get(item, ["options"])) {
      forEach(get(item, ["options"]), (opt) => {
        values = unselectItem(opt, values);
      });
      return values;
    } else {
      return filter(values, (v) => v !== item.id);
    }
  };

  const renderValue = (selected) => {
    let displayedValue = "";
    if (checked) {
      displayedValue = I18n.get("common_all");
    } else {
      displayedValue = getDisplayedValue({ options }, selected);
    }
    return displayedValue;
  };

  const getDisplayedValue = (item, selected) => {
    if (get(item, ["options"])) {
      let displayedValue = "";
      forEach(get(item, ["options"]), (opt) => {
        displayedValue += getDisplayedValue(opt, selected);
      });
      return displayedValue;
    }
    return includes(selected, item.id) ? `${item.name}, ` : "";
  };

  return (
    <TextField
      select
      fullWidth
      name={name}
      className={classes.textField}
      classes={{ root: classes.textFieldRoot }}
      value={values}
      onChange={onChange}
      variant="outlined"
      margin="dense"
      data-automation={dataAutomation}
      disabled={disabled}
      InputProps={{
        className: classes.input,
      }}
      SelectProps={{
        onBlur: props.onBlur,
        classes: {
          root: !props.value ? classes.placeholder : classes.selectInput,
          select: classes.select,
          selectMenu: classes.selectMenu,
        },
        MenuProps: {
          autoFocus: false,
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          MenuListProps: {
            classes: { root: classes.menuList },
            id: "selectMenuList",
          },
        },
        open: open,
        onOpen: onOpen,
        onClose: onClose,
        displayEmpty: true,
        renderValue: renderValue,
        multiple: true,
      }}
    >
      <MenuItem classes={{ root: classes.menuItemTop }}>
        <Grid container alignItems="center">
          <Grid item>
            <Checkbox
              size="small"
              color="primary"
              checked={checked || indeterminate}
              indeterminate={indeterminate}
              onChange={() => handleChange({ options })}
            />
          </Grid>
          <Grid item xs />
        </Grid>
      </MenuItem>
      {map(noSort ? options : sortBy(options, (opt) => opt.name), (opt) => (
        <MultiLevelSelectItem
          key={opt.id}
          item={opt}
          values={values}
          onChange={onChange}
          level={0}
          selectItem={selectItem}
          unselectItem={unselectItem}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
        />
      ))}
    </TextField>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(MultiLevelSelect));
